import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { Status } from "@/lib/api/types";
import { usePrefersReducedMotion } from "@/hooks/use-reduced-motion";

const categoryColors: Record<Status["category"], string> = {
  backlog: "bg-gray-400",
  unstarted: "bg-blue-500",
  started: "bg-yellow-500",
  completed: "bg-green-500",
  canceled: "bg-red-500",
};

// Actual color values for smooth framer-motion backgroundColor transitions
const categoryHex: Record<Status["category"], string> = {
  backlog: "#9ca3af",
  unstarted: "#3b82f6",
  started: "#eab308",
  completed: "#22c55e",
  canceled: "#ef4444",
};

interface StatusDotProps {
  status: Status;
  className?: string;
}

export function StatusDot({ status, className }: StatusDotProps) {
  const prefersReduced = usePrefersReducedMotion();

  if (prefersReduced) {
    return (
      <span
        className={cn(
          "inline-block h-2.5 w-2.5 rounded-full flex-shrink-0",
          categoryColors[status.category],
          className
        )}
        role="img"
        aria-label={`Status: ${status.name}`}
        title={status.name}
      />
    );
  }

  return (
    <motion.span
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full flex-shrink-0",
        className
      )}
      initial={{ backgroundColor: categoryHex[status.category] }}
      animate={{ backgroundColor: categoryHex[status.category] }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      role="img"
      aria-label={`Status: ${status.name}`}
      title={status.name}
    />
  );
}
